import { useAccount, usePublicClient, useWalletClient } from 'wagmi';
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { getContract } from 'viem';
import { Delegate, setDelegates } from "../state/delegateCash";
import { DELEGATE_CASH, WARM_XYZ } from "../app/globals";
import warm_abi from "../contracts/abis/warm_xyz.json";
import delegate_abi from "../contracts/abis/delegate_cash.json";

import { setWarmWalletConfig } from "../state/warmXYZ";

// Delegation services base interface
export interface WalletConfig {
  coldWallet: string[];
  hotWallet: string;
}
// WalletBalances interface is an object with keys of all wallets (connected , delegate.cash, and warm.xyz) that we get back and their respective balance amounts.
export interface WalletBalances {
  address: string | null | undefined;
  balance: number[] | number;
  isDelegate?: boolean;
}
// ActiveBalances interface is all the wallet who have a balance greater than 0 and their key indexes based of balance amount
export interface ActiveBalances {
  address: string | null | undefined;
  balance: number[] | number;
  keys: number[];
  isDelegate?: boolean;
}

export const flattenDelegates = (delegates: Delegate[]): WalletConfig => {
  const wc: WalletConfig = {
    hotWallet: "",
    coldWallet: [],
  };

  delegates.forEach((d: Delegate) => {
    // no hot wallet defined, initializing new walletConfig
    if (!wc.hotWallet) {
      wc.hotWallet = d.delegate;
      wc.coldWallet.push(d.vault);
    }
    // existing hot wallet defined, adding new cold wallet
    else if (
      wc.coldWallet.includes(d.delegate) &&
      wc.hotWallet === d.delegate
    ) {
      wc.coldWallet.push(d.vault);
    }
  });
  return wc;
};

const Web3Automation = (): JSX.Element => {
  const dispatch = useDispatch();
  const { address } = useAccount();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();

  useEffect(() => {
    // delegate.cash
    const combinedPromises = async () => {
      if (!publicClient || !address) return;

      const fetchDelegateCash = async () => {
        const contract = getContract({
          address: DELEGATE_CASH as `0x${string}`,
          abi: delegate_abi,
          client: publicClient,
        });
        const delegates = await contract.read.getDelegationsByDelegate([address]) as Delegate[];
        const delegateCashWalletConfig = flattenDelegates(delegates);
        dispatch(setDelegates(delegateCashWalletConfig));
        return delegateCashWalletConfig;
      };

      // warm.xyz
      const fetchHotWallets = async () => {
        let res: WalletConfig = {
          coldWallet: [],
          hotWallet: "",
        };
        if (typeof address === "string") {
          const contract = getContract({
            address: WARM_XYZ as `0x${string}`,
            abi: warm_abi,
            client: publicClient,
          });
          const coldWallets = await contract.read.getColdWallets([address]) as string[];
          if (coldWallets.length > 0) {
            res = {
              coldWallet: coldWallets,
              hotWallet: address,
            };
          }
          dispatch(setWarmWalletConfig(res));
        }
        return res;
      };

      await Promise.all([fetchDelegateCash(), fetchHotWallets()]);
    };
    if (!publicClient || !address) return;
    combinedPromises();
  }, [address, publicClient]);

  /**
   * Managing state and rendering of uwu data got tangled since react would preserve the
   * values and no reinitialize. Added so on wallet change we can just set the data according
   * to wallet connected and services that wallet is connected to
   */
  useEffect(() => {
    if (window.ethereum) {
      window.ethereum.on("chainChanged", () => {
        window.location.reload();
      });
      window.ethereum.on("accountsChanged", () => {
        window.location.reload();
      });
    }
  });
  return <div />;
};
export default Web3Automation;
